import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Loader2, ShieldCheck, PenLine } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Join() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [period, setPeriod] = useState("month");
  const [viewed, setViewed] = useState(false);
  const [checks, setChecks] = useState({});
  const [fullName, setName] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const scrollRef = useRef(null);
  const today = new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    (async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          fetch(`${API}/billing/plans`).then((r) => r.json()),
          fetch(`${API}/onboarding/agreement`).then((r) => r.json()),
        ]);
        const list = Array.isArray(pRes) ? pRes : pRes.plans || pRes.catalog || [];
        const found = list.find((p) => p.id === planId);
        if (!found || !["subscription", "program"].includes(found.type)) setNotFound(true);
        else setPlan(found);
        setAgreement(aRes);
      } catch {
        setNotFound(true);
      }
    })();
  }, [planId]);

  const price = useMemo(() => {
    if (!plan) return 0;
    if (plan.type === "subscription") return plan.prices?.[period] ?? 0;
    return plan.price ?? 0;
  }, [plan, period]);

  const allChecked = agreement && agreement.declarations.every((d) => checks[d.key]);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = viewed && allChecked && fullName.trim().length >= 2 && emailValid && signature.trim().length >= 2;

  const onScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setViewed(true);
  };

  const submit = async () => {
    setError("");
    if (!ready) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/onboarding/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: planId, period, full_name: fullName.trim(), email: email.trim(), signature: signature.trim(),
          checkboxes: checks, origin_url: window.location.origin,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Something went wrong.");
      window.location.href = d.checkout_url;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA] flex flex-col items-center justify-center px-6" data-testid="join-notfound">
        <Logo className="h-8 w-auto" />
        <p className="mt-6 text-zinc-400 font-light">This membership link isn't available. Please check the link or view our <Link to="/#pricing" className="text-[#9fd3ff]">programs</Link>.</p>
      </div>
    );

  if (!plan || !agreement)
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="h-6 w-6 text-zinc-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="px-6 lg:px-8 h-20 flex items-center max-w-4xl mx-auto w-full">
        <Link to="/" aria-label="MBM home"><Logo className="h-6 w-auto" /></Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24" data-testid="join-page">
        <div className="section-label text-[10px] text-[#9fd3ff]">Student Onboarding</div>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-light tracking-tight">
          Join {plan.name}.
        </h1>
        <p className="mt-3 text-sm text-zinc-500 font-light">
          Please read and sign the Membership Agreement &amp; Risk Disclosure below. Payment can only be completed after signing.
        </p>

        {/* Plan + price */}
        <div className="mt-8 glass-card p-5 flex items-center justify-between flex-wrap gap-4" data-testid="join-plan">
          <div>
            <div className="text-white font-medium">{plan.name}</div>
            <div className="text-xs text-zinc-500">{plan.type === "program" ? "Coaching program · billed monthly until cancelled" : "Auto-renewing subscription"}</div>
          </div>
          <div className="flex items-center gap-3">
            {plan.type === "subscription" && (
              <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                {["week", "month", "year"].map((p) => (
                  <button key={p} onClick={() => setPeriod(p)}
                    className={`px-3 py-1.5 rounded-md text-xs capitalize transition-colors ${period === p ? "bg-[#FAFAFA] text-[#0A0A0A]" : "text-zinc-400 hover:text-white"}`}
                    data-testid={`join-period-${p}`}>{p}</button>
                ))}
              </div>
            )}
            <div className="text-right">
              <span className="font-heading text-2xl">A${price}</span>
              <span className="text-xs text-zinc-500"> / {plan.type === "subscription" ? period : "month"}</span>
            </div>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-zinc-400" data-testid="join-renewal-note">
          Renews automatically each {plan.type === "subscription" ? period : "month"} until cancelled. Cancel anytime via Manage Billing in your account or support@mindbeforemarkets.com.
        </p>

        {/* Agreement */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500">
            <ShieldCheck className="h-4 w-4 text-[#9fd3ff]" />
            MBM Student Membership Agreement &amp; Risk Disclosure (v{agreement.version})
          </div>
          <div ref={scrollRef} onScroll={onScroll} data-testid="join-agreement-scroll"
            className="h-80 overflow-y-auto rounded-xl border border-white/10 bg-[#0B0B0C] p-5 space-y-4 text-sm">
            {agreement.sections.map((s) => (
              <div key={s.n}>
                <div className="text-white font-medium">{s.n}. {s.title}</div>
                {s.body.map((line, i) => (
                  <p key={i} className="mt-1 text-zinc-400 font-light leading-relaxed">{line}</p>
                ))}
              </div>
            ))}
          </div>
          {!viewed && <p className="mt-2 text-[11px] text-[#e6c67a]" data-testid="join-scroll-hint">↓ Scroll to the bottom of the agreement to continue.</p>}
          {viewed && <p className="mt-2 text-[11px] text-[#7ee0a1] flex items-center gap-1"><Check className="h-3 w-3" /> Agreement viewed in full.</p>}
        </div>

        {/* Declarations */}
        <div className="mt-8 space-y-2">
          {agreement.declarations.map((d) => (
            <label key={d.key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checks[d.key] ? "border-[#7ee0a1]/40 bg-[#7ee0a1]/[0.05]" : "border-white/10 hover:border-white/20"} ${!viewed ? "opacity-50 pointer-events-none" : ""}`}>
              <input type="checkbox" checked={!!checks[d.key]} disabled={!viewed}
                onChange={(e) => setChecks((c) => ({ ...c, [d.key]: e.target.checked }))}
                className="mt-0.5 accent-[#7ee0a1] h-4 w-4" data-testid={`join-check-${d.key}`} />
              <span className="text-sm text-zinc-300 font-light">{d.label}</span>
            </label>
          ))}
        </div>

        {/* E-signature */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3 text-xs text-zinc-500"><PenLine className="h-4 w-4 text-[#9fd3ff]" /> Electronic Signature</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="section-label text-[9px] text-zinc-500">Full Name</label>
              <input value={fullName} onChange={(e) => setName(e.target.value)} data-testid="join-fullname"
                className="mt-1.5 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="section-label text-[9px] text-zinc-500">Email Address</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="join-email"
                className="mt-1.5 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none" />
            </div>
            <div>
              <label className="section-label text-[9px] text-zinc-500">Date</label>
              <input value={today} readOnly data-testid="join-date"
                className="mt-1.5 w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-sm text-zinc-400 outline-none" />
            </div>
            <div>
              <label className="section-label text-[9px] text-zinc-500">Digital Signature (type your full name)</label>
              <input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="join-signature"
                style={{ fontFamily: "Georgia, serif" }}
                className="mt-1.5 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-lg italic outline-none" />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-zinc-600">Your IP address and a timestamp are recorded automatically with your signature.</p>
        </div>

        {error && <div className="mt-6 text-sm text-red-400 font-light" data-testid="join-error">{error}</div>}

        <button onClick={submit} disabled={!ready || busy} data-testid="join-submit"
          className="btn-solid mt-8 w-full py-4 text-sm font-medium tracking-wide disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Preparing secure checkout…</> : `Sign & continue to payment · A$${price} / ${plan.type === "subscription" ? period : "month"}`}
        </button>
        {!ready && <p className="mt-3 text-center text-[11px] text-zinc-600">Read the agreement, tick all boxes, and sign to enable payment.</p>}
      </main>
    </div>
  );
}
